import React, { useState, useEffect, useCallback } from "react"
import { useLocation } from "react-router-dom"
import { Container, Table, Alert, Pagination, PaginationItem, PaginationLink } from "reactstrap"
import { useMutation, useQuery } from "@tanstack/react-query"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"
import { debounce } from "lodash"

import { getProducts, deleteProduct } from "services/products"
import { Loading } from "components"
import { CreateModalForm, EditModalForm, ImportModalForm } from "components/products"
import storage from "services/storage"
import { authUserKey } from "config/API"
import ListHeader from "ui-componets/list-header"
import ProductsItemList from "components/products/products-item-list"
import Shortcuts from "components/Shortcuts"

const ProductsList = () => {
  document.title = "Lista de productos | Punto de venta"

  const Location = useLocation()

  const [filters, setFilters] = useState("")
  const [searchTxt, setSearchTxt] = useState(new URLSearchParams(Location.search).get("search") ?? "")
  const [modalCreate, setmodalCreate] = useState(false)
  const [modalImport, setmodalImport] = useState(false)
  const [modalEdit, setmodalEdit] = useState(false)
  const [productId, setproductId] = useState(null)
  const [showAlertSuccess, setShowAlertSuccess] = useState(false)
  const [msgSuccess, setMsgSuccess] = useState("")
  const ModalProduct = withReactContent(Swal)
  const styleCenterTable = { textAlign: "center", verticalAlign: "middle" }

  const products = useQuery(["products", filters, filters.toString()], () => getProducts(filters))
  const productDeleted = useMutation((id) => deleteProduct(id))

  const permissions = storage.get(authUserKey).user_permissions || []
  const canAdd = permissions.some((e) => e.codename === "add_product")
  const canEdit = permissions.some((e) => e.codename === "change_product")
  const canDelete = permissions.some((e) => e.codename === "delete_product")

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener("mouseenter", Swal.stopTimer)
      toast.addEventListener("mouseleave", Swal.resumeTimer)
    },
  })

  const toggleModalCreate = () => {
    setmodalCreate(!modalCreate)
  }

  const toggleModalImport = () => {
    setmodalImport(!modalImport)
  }

  const toggleModalEdit = () => {
    if (modalEdit) {
      setproductId("")
    }
    setmodalEdit(!modalEdit)
  }

  const openEditModal = (productId) => {
    setproductId(productId)
    toggleModalEdit()
  }

  const debouncedSearch = useCallback(
    debounce((text) => {
      const params = new URLSearchParams()
      params.set("search", text)
      params.delete("offset")
      params.delete("per_page")
      window.history.replaceState({}, null, `${Location.pathname}?${params}`)
      setFilters(params)
    }, 300),
    []
  )

  function handleChangeSearch(event) {
    setSearchTxt(event.target.value)
    debouncedSearch(event.target.value)
  }

  const openDeleteModal = (row) => {
    Swal.fire({
      title: `¿Está seguro que desea eliminar al producto: <b>${row.name}</b>?`,
      text: "Este cambio no se podrá revertir",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Aceptar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        productDeleted.mutateAsync(row?.id).then(() => {
          products.refetch()
          Swal.fire("!Eliminado!", `El producto <b>${row.name}</b> ha sido eliminado correctamente`, "success")
        }).catch((error)=>{
          Toast.fire("Error", error?.errors[0]?.message, "error")
        })
      }
    })
  }

  const handleSearch = (pageParams) => {
    const params = new URLSearchParams(filters)
    if (pageParams) {
      const newParams = new URLSearchParams(pageParams)
      window.history.replaceState({}, null, `${Location.pathname}?${newParams}`)
      setFilters(newParams)
    } else if (searchTxt) {
      params.set("search", searchTxt)
      params.delete("offset")
      params.delete("per_page")
      window.history.replaceState({}, null, `${Location.pathname}?${params}`)
      setFilters(params)
    } else {
      if (params.get("search")) {
        params.delete("offset")
        params.delete("per_page")
        params.delete("search")
        window.history.replaceState({}, null, `${Location.pathname}?${params}`)
        setFilters(params)
      }
    }
  }

  useEffect(() => {
    setFilters(Location.search)
  }, [])

  useEffect(() => {
    if (showAlertSuccess) {
      ModalProduct.fire({
        icon: "success",
        title: msgSuccess,
        didOpen: () => {
          products.refetch()
          setShowAlertSuccess(false)
        },
        confirmButtonText: "Aceptar",
        confirmButtonColor: "green",
      })
    }
  }, [showAlertSuccess])

  const isLoading = products.isLoading

  return !permissions.some((e) => e.codename === "view_product") ? (
    <div className="page-content">
      <div className="text-center">
        <h1>No autorizado para ver esta pagina</h1>
      </div>
    </div>
  ) : (
    <React.Fragment>
      <div className="page-content">
        {canAdd && (
          <CreateModalForm
            isOpen={modalCreate}
            toggle={toggleModalCreate}
            refetch={products.refetch}
            setShowAlertSuccess={setShowAlertSuccess}
            setMsgSuccess={setMsgSuccess}
          />
        )}
        {canAdd && (
          <ImportModalForm
            isOpen={modalImport}
            toggle={toggleModalImport}
            refetch={products.refetch}
            setShowAlertSuccess={setShowAlertSuccess}
            setMsgSuccess={setMsgSuccess}
          />
        )}
        {canEdit && productId && (
          <EditModalForm
            isOpen={modalEdit}
            toggle={toggleModalEdit}
            productId={productId}
            refetch={products.refetch}
            setShowAlertSuccess={setShowAlertSuccess}
            setMsgSuccess={setMsgSuccess}
          />
        )}
        <Container fluid>
          <Alert color="danger" isOpen={products.isError}>
            {products?.error?.errors?.map((error) => error?.message)}
          </Alert>
          <Alert color="success" isOpen={showAlertSuccess} toggle={() => setShowAlertSuccess(false)}>
            {msgSuccess}
          </Alert>

          <h2>Lista de Productos</h2>
          <div className="bg-white p-3">
            <ListHeader
              canAdd={canAdd}
              handleOnChangeSearch={handleChangeSearch}
              handleSearch={handleSearch}
              searchTxt={searchTxt}
              toggleModalCreate={toggleModalCreate}
              toggleModalImport={toggleModalImport}
            />
            {!products.error && isLoading ? (
              <div className="my-5 text-center">
                <Loading isLoading={isLoading} size={2} />
              </div>
            ) : products.error && !isLoading ? (
              <div className="my-5 text-center">
                <h1>Error intenta mas tarde</h1>
              </div>
            ) : (
              <div className="table-responsive" style={{ marginTop: "30px" }}>
                <Table>
                  <thead>
                    <tr>
                      <th style={styleCenterTable}>Código de barras</th>
                      <th style={styleCenterTable}>Nombre</th>
                      <th style={styleCenterTable}>Código de SAT</th>
                      <th style={styleCenterTable}>Costo</th>
                      <th style={styleCenterTable}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products?.data?.results?.length ? (
                      products?.data?.results?.map((item) => {
                        return (
                          <ProductsItemList
                            key={item.id}
                            item={item}
                            canDelete={canDelete}
                            canEdit={canEdit}
                            openDeleteModal={openDeleteModal}
                            openEditModal={openEditModal}
                          />
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center">
                          No hay datos
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                <div className="text-center">
                  {(products.data?.next || products.data?.previous) && (
                    <Pagination aria-label="Page navigation" className="d-flex justify-content-center">
                      {products.data?.previous && (
                        <PaginationItem>
                          <PaginationLink
                            previous
                            onClick={() => handleSearch(products.data?.previous.split("?")[1])}
                          />
                        </PaginationItem>
                      )}
                      {products.data?.next && (
                        <PaginationItem>
                          <PaginationLink next onClick={() => handleSearch(products.data?.next.split("?")[1])} />
                        </PaginationItem>
                      )}
                    </Pagination>
                  )}
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>
      <Shortcuts
        toggleModalCreate={toggleModalCreate}
        toggleModalImport={toggleModalImport}
      />
    </React.Fragment>
  )
}

export default ProductsList
